import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChartCard } from '../components/ChartCard';
import { Dona } from '../components/Dona';
import { Barras, FilaBarra } from '../components/Barras';
import { Ventana } from '../components/Ventana';
import { textoDeError } from '../components/Dialogo';
import { api } from '../lib/api';
import { ponerPeriodo } from '../components/PeriodPicker';
import { COLOR_PERSONA, RAMPA, dinero, entero, porcentaje } from '../lib/graficas';

/**
 * Analítica → PERFILES (0.46.0, perfiles v3). El perfil se decide SÓLO con quién es la persona (sexo, edad, estado civil,
 * hijos, etapa de vida); lo que pide o se hace ya no lo decide. Aquí: la dona de perfiles (con % en cada rebanada y una
 * leyenda que no se mueve al apagar una fila), y quiénes son — sexo, edad y a qué se dedican (agrupado en categorías que
 * se leen; tocar una barra enseña lo que dijo cada quien). Tocar un perfil abre su retrato con gráficas.
 */
export interface Demografia {
  total: number;
  sexo: { sabe: number; porNombre: number; filas: FilaBarra[] };
  edad: { sabe: number; filas: FilaBarra[] };
  ocupacion: { sabe: number; filas: (FilaBarra & { valores: { texto: string; n: number }[] })[] };
}
export const nombrePersona = (p: string) => (p === 'Sin datos' ? 'Sin perfil (faltan datos)' : p);

export function TabPerfiles({ personas, demografia, ocultos, setOcultos, periodo, onAbrir }: {
  personas: { persona: string; n: number }[]; demografia?: Demografia; ocultos: Set<string>; setOcultos: (f: (s: Set<string>) => Set<string>) => void;
  periodo: string; onAbrir: (p: string) => void;
}) {
  const [ocup, setOcup] = useState<{ nombre: string; n: number; valores: { texto: string; n: number }[] } | null>(null);
  const dm = demografia;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 340px), 1fr))', gap: 14 }}>
      <ChartCard title="¿A qué perfil se parece cada persona?" datos="perfiles" data={personas}
        como="Cada rebanada es un perfil y su %. Tocar un nombre lo esconde (los % se recalculan con lo que se ve); «Ver» abre quiénes son."
        vacio="Nadie tuvo actividad en este periodo (alta, cita o compra)."
        foot={<span data-perfil-fuente>
          <strong>No son pacientes:</strong> un buyer persona es un perfil tipo de cliente (Luz, Sandy, Luisa, Mario) para decidir cómo
          hablarle a cada grupo. <strong>Se decide sólo con quién es la persona</strong>: su sexo (capturado o por su nombre de pila) y su
          edad; en los rangos que se encimen, su estado civil, sus hijos y si estudia, se dedica al hogar o vive de su jubilación. Lo que pide o
          se hace ya no decide el perfil: se enseña al abrirlo. Cuenta a quien tuvo actividad en el periodo (alta, cita o compra).
          «Sin perfil» es quien no tiene con qué decidirlo; al abrirlo dice qué falta y qué pedir.</span>}
        render={(h) => (
          <div data-perfiles-dona>
            <Dona datos={personas.map((p) => ({ clave: p.persona, nombre: nombrePersona(p.persona), n: p.n }))} alto={h} unidad="personas"
              ocultos={ocultos} setOcultos={setOcultos} onAbrir={onAbrir} fijos={COLOR_PERSONA} datosTest="perfiles" />
          </div>
        )} />
      {dm && (
        <ChartCard title="¿Mujeres u hombres?" datos="sexo" data={dm.sexo.filas}
          como={`Se sabe de ${entero(dm.sexo.sabe)} de ${entero(dm.total)} (${porcentaje(dm.sexo.sabe, dm.total)}).${dm.sexo.porNombre ? ` En ${entero(dm.sexo.porNombre)} de esos casos lo dice su nombre de pila (no estaba capturado).` : ''}`}
          vacio="Nadie tiene el sexo capturado y ningún nombre lo dice claro."
          render={(h) => <Dona datos={dm.sexo.filas.map((f) => ({ ...f, color: f.clave === 'F' ? '#8E1A5F' : '#2a6fb5' }))} alto={Math.min(h, 240)} unidad="con dato" datosTest="sexo" />} />
      )}
      {dm && (
        <ChartCard title="¿Qué edad tienen?" datos="edad" data={dm.edad.sabe ? dm.edad.filas : []}
          como="Con la fecha de nacimiento (o la edad capturada). La barra más oscura, más edad."
          vacio={`Nadie de este periodo tiene edad ni fecha de nacimiento: se pide al registrarse en DraIA.`}
          render={() => <Barras filas={dm.edad.filas.map((f, i) => ({ ...f, color: RAMPA[i] }))} sabe={dm.edad.sabe} de={dm.total} datos="edad" />} />
      )}
      {dm && (
        <ChartCard title="¿A qué se dedican?" datos="ocupacion" data={dm.ocupacion.filas}
          como="Lo que dijeron al registrarse en DraIA, agrupado. Toca una barra para ver lo que dijo cada quien. No separa perfiles: describe."
          vacio="Nadie de este periodo dijo a qué se dedica (DraIA lo pregunta al registrarse)."
          render={() => <Barras filas={dm.ocupacion.filas} sabe={dm.ocupacion.sabe} de={dm.total} datos="ocupacion"
            onAbrir={(k) => { const f = dm.ocupacion.filas.find((x) => x.clave === k); if (f) setOcup(f); }} />} />
      )}
      {ocup && <ValoresOcupacion f={ocup} onClose={() => setOcup(null)} />}
      <span hidden data-periodo-perfiles={periodo} />
    </div>
  );
}

/** Lo que dijo cada quien dentro de una categoría de ocupación (el texto tal como está en DraIA). */
export function ValoresOcupacion({ f, onClose }: { f: { nombre: string; n: number; valores: { texto: string; n: number }[] }; onClose: () => void }) {
  return (
    <Ventana datos="ocupacion" ancho={560} onClose={onClose} titulo={<>{f.nombre} · {entero(f.n)} {f.n === 1 ? 'persona' : 'personas'}</>}
      subtitulo="Así lo dijeron al registrarse (mismo texto que en DraIA).">
      <Barras filas={f.valores.map((v) => ({ clave: v.texto, nombre: v.texto, n: v.n }))} datos="valores" />
    </Ventana>
  );
}

// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────────
// El retrato de un perfil.
// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────────
interface Definicion {
  clave: string; titulo: string; edad: string; quienEs: string; busca: string;
  tratamientos: string[]; canales: string[]; objeciones: string[]; detonantes: string[]; frase: string;
}
interface Grafica { sabe: number; filas: FilaBarra[] }
interface PersonaResp {
  persona: string;
  definicion?: Definicion | null;
  graficas?: {
    total: number; edadPromedio: number | null;
    edad: Grafica; sexo: Grafica & { porNombre: number }; estadoCivil: Grafica; hijos: Grafica; escolaridad: Grafica;
    ocupacion: { sabe: number; noEsOcupacion?: number; filas: (FilaBarra & { valores: { texto: string; n: number }[] })[] };
    zona: Grafica; canales: Grafica; etapas: Grafica; piden: Grafica; seHacen: Grafica;
    compran: { sabe: number; filas: (FilaBarra & { ingreso: number })[] }; ltv: Grafica;
  };
  totales: { pacientes: number; compras: number; ingreso: number; ticketPromedio: number; ltvPromedio: number; sinCompras: number };
  pacientes: { patientId: string; nombre: string; status: string | null; asesora: string | null; razones: string[]; compras: number; ingreso: number; ultimaCompra: string | null }[];
  faltan?: Faltan | null;
  nota: string;
}
export interface Faltan { total: number; sexo: number; edad: number; todo: number; soloEdad: number; soloSexo?: number; noSeParece?: number; halory: number; conAnuncio: number; leads: number; sinNombre: number; conCita: number; conDraia: number }

const ETAPA_TXT: Record<string, string> = { LEAD: 'Lead', PV: 'Primera visita', SUB: 'Recompra', UGC: 'UGC' };

/** Del segmento a las personas: quiénes son (con gráficas), qué piden, qué compran y la lista con nombre. */
export function PersonaVentana({ persona, periodo, onClose }: { persona: string; periodo: string; onClose: () => void }) {
  const [d, setD] = useState<PersonaResp | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [ocup, setOcup] = useState<{ nombre: string; n: number; valores: { texto: string; n: number }[] } | null>(null);
  const [verDef, setVerDef] = useState(false);
  useEffect(() => {
    const qs = ponerPeriodo(new URLSearchParams({ nombre: persona }), periodo);
    api<PersonaResp>(`analytics/persona?${qs}`).then(setD).catch((e) => setErr(textoDeError(e)));
  }, [persona, periodo]);
  const sinDatos = (d?.persona || persona) === 'Sin datos';
  const g = d?.graficas;
  const color = COLOR_PERSONA[d?.persona || persona] || '#8b7355';
  const Mini = ({ titulo, children, datos }: { titulo: string; children: React.ReactNode; datos?: string }) => (
    <div className="pv-mini" data-mini={datos || ''}><h4>{titulo}</h4>{children}</div>
  );
  return (
    <Ventana datos="persona" ancho={980} onClose={onClose}
      titulo={sinDatos ? 'Sin perfil (faltan datos)' : `Perfil «${d?.persona || persona[0] + persona.slice(1).toLowerCase()}»`}
      subtitulo={<>Un buyer persona es un <strong>perfil tipo de cliente</strong>, no una persona real. Abajo, con gráficas, quiénes caen en él de verdad; al final, con nombre.</>}>
      <style>{`
        .pv-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 280px), 1fr)); gap: 12px; margin: 10px 0 14px; }
        .pv-mini { border: 1px solid var(--border); border-radius: 14px; padding: 12px 14px; min-width: 0; background: #fff; }
        .pv-mini h4 { margin: 0 0 8px; font-size: 13.5px; color: var(--gold-dark); font-weight: 600; }
        .pv-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(118px, 1fr)); gap: 8px; margin-bottom: 10px; }
        .pv-kpi { background: var(--bg-alt); border-radius: 12px; padding: 9px 11px; min-width: 0; }
        .pv-kpi span { display: block; font-size: 11px; color: var(--ink-soft); }
        .pv-kpi b { display: block; font-size: 18px; color: var(--ink); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      `}</style>
      {err && <div style={{ padding: 12, color: 'var(--warning)', background: 'var(--warning-bg)', borderRadius: 10 }}>{err}</div>}
      {!d && !err && <div className="muted" style={{ padding: 30, textAlign: 'center' }}>Cargando…</div>}
      {d && g && (
        <>
          {d.definicion && (
            <div style={{ borderLeft: `3px solid ${color}`, padding: '6px 0 6px 12px', marginBottom: 6 }}>
              <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--gold-dark)' }}>{d.definicion.titulo} <span className="badge" style={{ marginLeft: 6 }}>{d.definicion.edad}</span></div>
              <p style={{ fontSize: 13, lineHeight: 1.55, margin: '6px 0 0' }}>{d.definicion.quienEs}</p>
              <button type="button" className="btn-ghost" style={{ padding: '3px 12px', fontSize: 12, marginTop: 6, boxShadow: 'none' }} aria-expanded={verDef} onClick={() => setVerDef((x) => !x)} data-ver-definicion>
                {verDef ? 'Ocultar cómo lo definió marketing' : 'Cómo lo definió marketing'}
              </button>
              {verDef && <Definida def={d.definicion} />}
            </div>
          )}
          {sinDatos && d.faltan && <QueFalta f={d.faltan} />}
          <div className="pv-kpis" data-pv-kpis>
            <div className="pv-kpi"><span>Personas</span><b>{entero(d.totales.pacientes)}</b></div>
            <div className="pv-kpi"><span>Edad promedio</span><b>{g.edadPromedio ? `${g.edadPromedio} años` : '—'}</b></div>
            <div className="pv-kpi"><span>Cobrado</span><b title={dinero(d.totales.ingreso)}>{dinero(d.totales.ingreso)}</b></div>
            <div className="pv-kpi"><span>LTV promedio</span><b>{dinero(d.totales.ltvPromedio)}</b></div>
            <div className="pv-kpi"><span>Ticket promedio</span><b>{dinero(d.totales.ticketPromedio)}</b></div>
          </div>
          <div className="pv-grid" data-pv-graficas>
            <Mini titulo="¿Qué edad tienen?" datos="edad"><Barras filas={g.edad.filas.map((f, i) => ({ ...f, color: RAMPA[i] }))} sabe={g.edad.sabe} de={g.total} vacio="Nadie tiene edad ni fecha de nacimiento." /></Mini>
            <Mini titulo="¿Mujeres u hombres?" datos="sexo">
              {g.sexo.filas.length ? <Dona datos={g.sexo.filas.map((f) => ({ ...f, color: f.clave === 'F' ? '#8E1A5F' : '#2a6fb5' }))} alto={150} unidad="con dato" datosTest="pv-sexo" /> : <p className="muted" style={{ fontSize: 12.5 }}>Nadie tiene el sexo capturado ni un nombre que lo diga.</p>}
              <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>Se sabe de {entero(g.sexo.sabe)} de {entero(g.total)}{g.sexo.porNombre ? ` · ${entero(g.sexo.porNombre)} por su nombre` : ''}</div>
            </Mini>
            <Mini titulo="¿A qué se dedican?" datos="ocupacion">
              <Barras filas={g.ocupacion.filas} sabe={g.ocupacion.sabe} de={g.total} vacio="Nadie dijo a qué se dedica."
                onAbrir={(k) => { const f = g.ocupacion.filas.find((x) => x.clave === k); if (f) setOcup(f); }} />
            </Mini>
            <Mini titulo="Estado civil" datos="civil"><Barras filas={g.estadoCivil.filas} sabe={g.estadoCivil.sabe} de={g.total} vacio="Nadie lo tiene capturado." /></Mini>
            <Mini titulo="Hijos" datos="hijos"><Barras filas={g.hijos.filas.map((f) => ({ ...f, color: RAMPA[Math.min(5, 1 + (Number(f.clave.slice(1)) || 0))] }))} sabe={g.hijos.sabe} de={g.total} vacio="Nadie lo tiene capturado." /></Mini>
            <Mini titulo="Escolaridad" datos="escolaridad"><Barras filas={g.escolaridad.filas} sabe={g.escolaridad.sabe} de={g.total} vacio="Nadie la tiene capturada." /></Mini>
            <Mini titulo="¿Dónde viven?" datos="zona"><Barras filas={g.zona.filas} sabe={g.zona.sabe} de={g.total} vacio="Nadie tiene colonia ni ciudad capturada." /></Mini>
            <Mini titulo="¿Por dónde llegaron?" datos="canales"><Barras filas={g.canales.filas} sabe={g.canales.sabe} de={g.total} vacio="Nadie tiene el canal capturado." /></Mini>
            <Mini titulo="¿Qué piden en sus citas?" datos="piden"><Barras filas={g.piden.filas} sabe={g.piden.sabe} de={g.total} vacio="Nadie pidió un tratamiento reconocible." /></Mini>
            <Mini titulo="¿Qué se hacen?" datos="hacen"><Barras filas={g.seHacen.filas} sabe={g.seHacen.sabe} de={g.total} vacio="Nadie tiene tratamientos en DraIA." /></Mini>
            <Mini titulo="¿Qué compran? (dinero real)" datos="compran">
              <Barras filas={g.compran.filas.map((f) => ({ ...f, n: f.ingreso, extra: `· ${f.n === 1 ? '1 persona' : `${entero(f.n)} personas`}` }))}
                formato={(f) => dinero(f.n)} sabe={g.compran.sabe} de={g.total} vacio="Nadie de este grupo tiene compras registradas." />
            </Mini>
            <Mini titulo="¿Cuánto vale cada quien? (LTV)" datos="ltv"><Barras filas={g.ltv.filas.map((f, i) => ({ ...f, color: RAMPA[i] }))} sabe={g.ltv.sabe} de={g.total} vacio="Nadie de este grupo tiene compras registradas." /></Mini>
          </div>
          {d.totales.sinCompras > 0 && (
            <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
              {entero(d.totales.sinCompras)} de {entero(d.totales.pacientes)} no tienen compras registradas. El LTV promedio es sólo de quien sí compró.
            </div>
          )}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560, fontSize: 13.5 }} data-pv-tabla>
              <thead><tr>
                {['Persona', 'Asesoría', 'Compras', 'Cobrado', 'Última compra'].map((t, i) => <th key={t} style={{ textAlign: i >= 2 && i <= 3 ? 'right' : 'left', padding: '6px 8px', fontSize: 12, color: 'var(--ink-soft)' }}>{t}</th>)}
              </tr></thead>
              <tbody>
                {d.pacientes.map((x) => (
                  <tr key={x.patientId}>
                    <td style={{ padding: '6px 8px', borderTop: '1px solid var(--border)' }}>
                      <Link to={`/paciente/${x.patientId}`} style={{ color: 'var(--gold-dark)', fontWeight: 500 }}>{x.nombre || '(sin nombre)'}</Link>
                      {x.status && <span className="muted" style={{ fontSize: 11 }}> · {ETAPA_TXT[x.status] || x.status}</span>}
                      {x.razones?.length > 0 && <div className="muted" style={{ fontSize: 11 }} data-razones>{x.razones.slice(0, 3).join(' · ')}</div>}
                    </td>
                    <td style={{ padding: '6px 8px', borderTop: '1px solid var(--border)' }} className="muted">{x.asesora || '—'}</td>
                    <td style={{ padding: '6px 8px', borderTop: '1px solid var(--border)', textAlign: 'right' }}>{x.compras}</td>
                    <td style={{ padding: '6px 8px', borderTop: '1px solid var(--border)', textAlign: 'right', fontWeight: 600 }}>{dinero(x.ingreso)}</td>
                    <td style={{ padding: '6px 8px', borderTop: '1px solid var(--border)' }} className="muted">{x.ultimaCompra || (x.compras ? '—' : 'sin compras')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {d.totales.pacientes > d.pacientes.length && <p className="muted" style={{ fontSize: 12 }}>Se enseñan {entero(d.pacientes.length)} de {entero(d.totales.pacientes)} (primero quien más ha pagado).</p>}
          <p className="muted" style={{ fontSize: 12, marginTop: 10 }}>{d.nota}</p>
        </>
      )}
      {ocup && <ValoresOcupacion f={ocup} onClose={() => setOcup(null)} />}
    </Ventana>
  );
}

/** El perfil tal como lo definió marketing (plegado: primero lo que dicen los datos). */
function Definida({ def }: { def: Definicion }) {
  const Bloque = ({ t, xs }: { t: string; xs: string[] }) => (
    <div style={{ marginTop: 6 }}>
      <strong style={{ fontSize: 12 }}>{t}</strong>
      <div className="muted" style={{ fontSize: 12.5, lineHeight: 1.55 }}>{xs.join(' · ')}</div>
    </div>
  );
  return (
    <div style={{ marginTop: 8 }} data-definicion>
      <p style={{ fontSize: 13, lineHeight: 1.6, margin: 0 }}><strong>Qué busca:</strong> {def.busca}</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 230px), 1fr))', gap: 10, marginTop: 4 }}>
        <Bloque t="Tratamientos que pensó marketing" xs={def.tratamientos} />
        <Bloque t="Dónde está" xs={def.canales} />
        <Bloque t="Qué frena la compra" xs={def.objeciones} />
        <Bloque t="Qué detona la compra" xs={def.detonantes} />
      </div>
      <p style={{ fontSize: 12.5, fontStyle: 'italic', color: 'var(--gold-dark)', margin: '8px 0 0' }}>{def.frase}</p>
    </div>
  );
}

/** "Sin perfil": qué falta, contado, y qué pedir para clasificarlas. */
function QueFalta({ f }: { f: Faltan }) {
  const pct = (n: number) => (f.total ? Math.round((n / f.total) * 100) : 0);
  const fila = (etq: string, n: number, dato: string) => (
    <div key={dato} data-falta={dato} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 8, alignItems: 'center', margin: '5px 0' }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12.5 }}>{etq}</div>
        <div style={{ height: 6, background: 'rgba(0,0,0,0.06)', borderRadius: 4, marginTop: 3 }}>
          <div style={{ width: `${pct(n)}%`, height: 6, background: 'var(--gold-dark)', borderRadius: 4 }} />
        </div>
      </div>
      <div style={{ fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{entero(n)} · {pct(n)} %</div>
    </div>
  );
  return (
    <div data-que-falta style={{ background: 'var(--gold-light)', borderRadius: 12, padding: '12px 14px', marginBottom: 12 }}>
      <div style={{ fontWeight: 600, color: 'var(--gold-dark)', marginBottom: 4 }}>¿Qué les falta?</div>
      <div style={{ fontSize: 13, marginBottom: 4 }}>De <strong>{entero(f.total)}</strong> personas sin perfil, les falta:</div>
      {fila('La edad (ni edad ni fecha de nacimiento)', f.edad, 'edad')}
      {fila('El sexo (no está capturado y el nombre no lo dice)', f.sexo, 'sexo')}
      {fila('Las dos cosas', f.todo, 'todo')}
      {f.noSeParece ? fila('Tienen los datos, pero su edad no se parece a ningún perfil', f.noSeParece, 'no-se-parece') : null}
      <div style={{ fontSize: 12.5, lineHeight: 1.6, marginTop: 8 }} data-falta-origen>
        <strong>De dónde vienen:</strong> {entero(f.halory)} llegaron por Halory ({entero(f.conAnuncio)} con el anuncio por el que llegaron)
        · {entero(f.leads)} son leads (nunca han venido) · {entero(f.conCita)} tienen una cita · {entero(f.conDraia)} tienen una consulta en DraIA
        · {entero(f.sinNombre)} llegaron «Sin nombre».
      </div>
      <div style={{ fontSize: 12.5, lineHeight: 1.6, marginTop: 8, padding: '8px 10px', background: '#fff', borderRadius: 10 }} data-que-pedir>
        <strong>Qué pedir para clasificarlas</strong>
        <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
          <li><strong>La fecha de nacimiento</strong> (o la edad): es lo que decide el perfil.{f.soloEdad ? ` A ${entero(f.soloEdad)} sólo les falta eso.` : ''}</li>
          <li><strong>El sexo</strong>, cuando el nombre no lo dice (nombres que se usan para los dos, apodos, «Sin nombre»).</li>
          <li>Sin edad, también decide que <strong>dos datos de su vida</strong> apunten al mismo perfil: estado civil, hijos, o si estudia, se dedica al hogar o vive de su jubilación.</li>
        </ul>
        <div style={{ marginTop: 4 }}>
          Dónde: al <strong>registrarse en DraIA</strong> (fecha de nacimiento, sexo, estado civil, a qué se dedica) y en <strong>Halory</strong>: que la IA pregunte la edad al agendar.
        </div>
      </div>
    </div>
  );
}
